import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { TopBar } from '../components/layout/TopBar';
import { useAppStore, useAuthStore } from '../store/useStore';
import { restaurantAPI, timeSlotsAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogFooter 
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { 
  Building2, 
  Clock, 
  Plus, 
  Trash2,
  Globe,
  CreditCard,
  Edit2,
  MapPin,
  Phone,
  Mail,
  Cake,
  Bell,
  Link2,
  ExternalLink,
  Check
} from 'lucide-react';
import { BirthdaySettings } from '../components/settings/BirthdaySettings';
import { AlertsSettings } from '../components/settings/AlertsSettings';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DAYS = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];

export default function Settings() {
  const { t } = useTranslation();
  const { user, token } = useAuthStore();
  const { timeSlots, setTimeSlots } = useAppStore();
  const [searchParams] = useSearchParams();
  const defaultTab = searchParams.get('tab') || 'general';
  
  const [restaurant, setRestaurant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSlotDialog, setShowSlotDialog] = useState(false);
  const [editingSlot, setEditingSlot] = useState(null);
  
  // Multi-location state
  const [locations, setLocations] = useState([]);
  const [showLocationDialog, setShowLocationDialog] = useState(false);
  const [locationForm, setLocationForm] = useState({
    location_name: '',
    address: '',
    phone: '',
    email: '',
    timezone: 'America/Mexico_City'
  });
  
  // Stripe Connect state
  const [stripeConnectStatus, setStripeConnectStatus] = useState({
    connected: false,
    account_id: null,
    charges_enabled: false,
    onboarding_complete: false
  });
  const [stripeLoading, setStripeLoading] = useState(false);

  const [restaurantForm, setRestaurantForm] = useState({
    name: '',
    timezone: 'America/Mexico_City',
    currency: 'MXN',
  });

  const [slotForm, setSlotForm] = useState({
    name: '',
    start_time: '12:00',
    end_time: '16:00',
    days: [0, 1, 2, 3, 4, 5, 6],
    buffer_minutes: 15,
    default_duration: 90,
    max_covers: 50,
    is_active: true,
  });

  useEffect(() => {
    fetchData();
    fetchStripeStatus();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [restaurantRes, slotsRes] = await Promise.all([
        restaurantAPI.get(),
        timeSlotsAPI.list(),
      ]);
      setRestaurant(restaurantRes.data);
      setRestaurantForm({
        name: restaurantRes.data.name,
        timezone: restaurantRes.data.timezone || 'America/Mexico_City',
        currency: restaurantRes.data.currency || 'MXN',
      });
      setTimeSlots(slotsRes.data);
      
      // Fetch locations if user has multi-location access
      if (user?.has_multiple_locations || user?.role === 'corporate_admin' || user?.role === 'owner') {
        await fetchLocations();
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchLocations = async () => {
    try {
      const res = await fetch(`${API_URL}/api/locations`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setLocations(data.locations || []);
      }
    } catch (err) {
      console.error('Error fetching locations:', err);
    }
  };

  const handleCreateLocation = async () => {
    try {
      const res = await fetch(`${API_URL}/api/locations`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(locationForm)
      });
      
      if (res.ok) {
        toast.success('Ubicación creada exitosamente');
        setShowLocationDialog(false);
        setLocationForm({
          location_name: '',
          address: '',
          phone: '',
          email: '',
          timezone: 'America/Mexico_City'
        });
        fetchLocations();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Error al crear ubicación');
      }
    } catch (err) {
      toast.error('Error al crear ubicación');
    }
  };

  const handleDeleteLocation = async (locationId) => {
    if (!confirm('¿Estás seguro de eliminar esta ubicación?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/locations/${locationId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success('Ubicación eliminada');
        fetchLocations();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Error al eliminar');
      }
    } catch (err) {
      toast.error('Error al eliminar ubicación');
    }
  };
  
  // Stripe Connect functions
  const fetchStripeStatus = async () => {
    try {
      const res = await fetch(`${API_URL}/api/restaurants/stripe/connect-status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setStripeConnectStatus(data);
      }
    } catch (err) {
      console.error('Error fetching stripe status:', err);
    }
  };
  
  const handleConnectStripe = async () => {
    setStripeLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/restaurants/stripe/connect`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        if (data.onboarding_url) {
          // Open Stripe onboarding in new tab
          window.open(data.onboarding_url, '_blank');
          toast.success('Abre la pestaña para completar la configuración de Stripe');
        }
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Error al conectar con Stripe');
      }
    } catch (err) {
      toast.error('Error al conectar con Stripe');
    } finally {
      setStripeLoading(false);
    }
  };
  
  const handleDisconnectStripe = async () => {
    if (!confirm('¿Estás seguro de desconectar Stripe? No podrás recibir pagos hasta reconectar.')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/restaurants/stripe/disconnect`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success('Stripe desconectado');
        setStripeConnectStatus({
          connected: false,
          account_id: null,
          charges_enabled: false,
          onboarding_complete: false
        });
      }
    } catch (err) {
      toast.error('Error al desconectar Stripe');
    }
  };

  const handleSaveRestaurant = async () => {
    try {
      await restaurantAPI.update(restaurantForm);
      toast.success('Configuración guardada');
    } catch (error) {
      toast.error('Error al guardar');
    }
  };

  const handleCreateSlot = async () => {
    try {
      const response = await timeSlotsAPI.create(slotForm);
      setTimeSlots([...timeSlots, response.data]);
      setShowSlotDialog(false);
      resetSlotForm();
      toast.success('Turno creado');
    } catch (error) {
      toast.error('Error al crear turno');
    }
  };

  const handleUpdateSlot = async () => {
    try {
      const response = await timeSlotsAPI.update(editingSlot.id, slotForm);
      setTimeSlots(timeSlots.map(s => s.id === editingSlot.id ? response.data : s));
      setShowSlotDialog(false);
      setEditingSlot(null);
      resetSlotForm();
      toast.success('Turno actualizado');
    } catch (error) {
      toast.error('Error al actualizar turno');
    }
  };

  const openEditSlot = (slot) => {
    setEditingSlot(slot);
    setSlotForm({
      name: slot.name,
      start_time: slot.start_time,
      end_time: slot.end_time,
      days: slot.days || [0, 1, 2, 3, 4, 5, 6],
      buffer_minutes: slot.buffer_minutes || 15,
      default_duration: slot.default_duration || 90,
      max_covers: slot.max_covers || 50,
      is_active: slot.is_active !== false,
    });
    setShowSlotDialog(true);
  };

  const resetSlotForm = () => {
    setSlotForm({
      name: '',
      start_time: '12:00',
      end_time: '16:00',
      days: [0, 1, 2, 3, 4, 5, 6],
      buffer_minutes: 15,
      default_duration: 90,
      max_covers: 50,
      is_active: true,
    });
  };

  const openNewSlot = () => {
    setEditingSlot(null);
    resetSlotForm();
    setShowSlotDialog(true);
  };

  const handleDeleteSlot = async (slotId) => {
    try {
      await timeSlotsAPI.delete(slotId);
      setTimeSlots(timeSlots.filter(s => s.id !== slotId));
      toast.success('Turno eliminado');
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const toggleDay = (dayIndex) => {
    if (slotForm.days.includes(dayIndex)) {
      setSlotForm({ ...slotForm, days: slotForm.days.filter(d => d !== dayIndex) });
    } else {
      setSlotForm({ ...slotForm, days: [...slotForm.days, dayIndex].sort() });
    }
  };

  return (
    <div className="min-h-screen" data-testid="settings-page">
      <TopBar title={t('settings.title')} helpSection="settings" />

      <div className="p-6">
        <Tabs defaultValue={defaultTab} className="space-y-6">
          <TabsList>
            <TabsTrigger value="general" data-testid="tab-general">
              <Building2 className="h-4 w-4 mr-2" /> {t('settings.general')}
            </TabsTrigger>
            <TabsTrigger value="timeslots" data-testid="tab-timeslots">
              <Clock className="h-4 w-4 mr-2" /> {t('settings.timeSlots')}
            </TabsTrigger>
            <TabsTrigger value="birthday" data-testid="tab-birthday">
              <Cake className="h-4 w-4 mr-2" /> Cumpleaños
            </TabsTrigger>
            <TabsTrigger value="alerts" data-testid="tab-alerts">
              <Bell className="h-4 w-4 mr-2" /> Alertas
            </TabsTrigger>
            <TabsTrigger value="payments" data-testid="tab-payments">
              <CreditCard className="h-4 w-4 mr-2" /> Pagos
            </TabsTrigger>
            {(user?.role === 'corporate_admin' || user?.role === 'owner' || user?.has_multiple_locations) && (
              <TabsTrigger value="locations" data-testid="tab-locations">
                <MapPin className="h-4 w-4 mr-2" /> Ubicaciones
              </TabsTrigger>
            )}
          </TabsList>

          {/* General Settings */}
          <TabsContent value="general" className="space-y-6">
            <Card data-testid="restaurant-settings-card">
              <CardHeader>
                <CardTitle className="text-base">Información del Restaurante</CardTitle>
                <CardDescription>Configura los datos básicos de tu restaurante</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>{t('auth.restaurantName')}</Label>
                  <Input
                    value={restaurantForm.name}
                    onChange={(e) => setRestaurantForm({ ...restaurantForm, name: e.target.value })}
                    placeholder="Mi Restaurante"
                    data-testid="restaurant-name-input"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Zona Horaria</Label>
                    <Select 
                      value={restaurantForm.timezone} 
                      onValueChange={(value) => setRestaurantForm({ ...restaurantForm, timezone: value })}
                    >
                      <SelectTrigger data-testid="timezone-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="America/Mexico_City">Ciudad de México (UTC-6)</SelectItem>
                        <SelectItem value="America/Cancun">Cancún (UTC-5)</SelectItem>
                        <SelectItem value="America/Tijuana">Tijuana (UTC-8)</SelectItem>
                        <SelectItem value="America/Bogota">Bogotá (UTC-5)</SelectItem>
                        <SelectItem value="America/Lima">Lima (UTC-5)</SelectItem>
                        <SelectItem value="Europe/Madrid">Madrid (UTC+1)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Moneda</Label>
                    <Select 
                      value={restaurantForm.currency} 
                      onValueChange={(value) => setRestaurantForm({ ...restaurantForm, currency: value })}
                    >
                      <SelectTrigger data-testid="currency-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="MXN">MXN (Peso Mexicano)</SelectItem>
                        <SelectItem value="USD">USD (Dólar)</SelectItem>
                        <SelectItem value="EUR">EUR (Euro)</SelectItem>
                        <SelectItem value="COP">COP (Peso Colombiano)</SelectItem>
                        <SelectItem value="PEN">PEN (Sol Peruano)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <Button onClick={handleSaveRestaurant} data-testid="save-restaurant-btn">
                  {t('common.save')}
                </Button>
              </CardContent>
            </Card>

            {/* API Keys Card */}
            <Card data-testid="api-keys-card">
              <CardHeader>
                <CardTitle className="text-base">Claves de API</CardTitle>
                <CardDescription>Usa estas claves para integrar el widget o conectar aplicaciones externas</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Restaurant ID</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <code className="flex-1 p-2 bg-muted rounded text-sm font-mono">
                      {user?.restaurant_id || 'Loading...'}
                    </code>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => {
                        navigator.clipboard.writeText(user?.restaurant_id || '');
                        toast.success('Copiado');
                      }}
                      data-testid="copy-restaurant-id-btn"
                    >
                      Copiar
                    </Button>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">
                  Usa este ID para el endpoint del widget: <code>/api/widget/availability/{'{restaurant_id}'}</code>
                </p>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Time Slots Settings */}
          <TabsContent value="timeslots" className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-medium">Turnos de Servicio</h3>
                <p className="text-sm text-muted-foreground">Configura los horarios disponibles para reservaciones</p>
              </div>
              <Button size="sm" onClick={openNewSlot} data-testid="add-timeslot-btn">
                <Plus className="h-4 w-4 mr-1" /> Agregar Turno
              </Button>
            </div>

            <div className="grid gap-4">
              {timeSlots.length === 0 ? (
                <Card>
                  <CardContent className="py-8 text-center text-muted-foreground">
                    No hay turnos configurados
                  </CardContent>
                </Card>
              ) : (
                timeSlots.map((slot) => (
                  <Card key={slot.id} data-testid={`timeslot-card-${slot.id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="flex items-center gap-3">
                            <Clock className="h-5 w-5 text-primary" />
                            <div>
                              <h4 className="font-medium">{slot.name}</h4>
                              <p className="text-sm text-muted-foreground">
                                {slot.start_time} - {slot.end_time}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 mt-2">
                            {DAYS.map((day, i) => (
                              <span
                                key={i}
                                className={`text-xs px-1.5 py-0.5 rounded ${
                                  slot.days?.includes(i) 
                                    ? 'bg-primary/20 text-primary' 
                                    : 'bg-muted text-muted-foreground'
                                }`}
                              >
                                {day}
                              </span>
                            ))}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`text-xs px-2 py-1 rounded ${slot.is_active ? 'bg-green-500/10 text-green-600' : 'bg-muted text-muted-foreground'}`}>
                            {slot.is_active ? 'Activo' : 'Inactivo'}
                          </span>
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => openEditSlot(slot)}
                            data-testid={`edit-timeslot-${slot.id}`}
                          >
                            <Edit2 className="h-4 w-4 text-primary" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => handleDeleteSlot(slot.id)}
                            data-testid={`delete-timeslot-${slot.id}`}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>

          {/* Birthday Settings */}
          <TabsContent value="birthday" className="space-y-4">
            <BirthdaySettings />
          </TabsContent>

          {/* Alerts Settings */}
          <TabsContent value="alerts" className="space-y-4">
            <AlertsSettings />
          </TabsContent>

          {/* Payments / Stripe Connect Settings */}
          <TabsContent value="payments" className="space-y-4">
            <Card data-testid="stripe-connect-card">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
                    <Link2 className="h-5 w-5 text-purple-600" />
                  </div>
                  <div>
                    <CardTitle className="text-base">Stripe Connect</CardTitle>
                    <CardDescription>Conecta tu cuenta de Stripe para recibir pagos de reservaciones y eventos</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                {stripeConnectStatus.connected ? (
                  <div className="space-y-4">
                    {/* Connected Status */}
                    <div className="flex items-center justify-between p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="h-8 w-8 rounded-full bg-green-500 flex items-center justify-center">
                          <Check className="h-4 w-4 text-white" />
                        </div>
                        <div>
                          <p className="font-medium text-green-700 dark:text-green-400">Cuenta Conectada</p>
                          <code className="text-xs text-muted-foreground">{stripeConnectStatus.account_id}</code>
                        </div>
                      </div>
                      <Button 
                        variant="outline" 
                        size="sm"
                        className="text-red-600 hover:text-red-700"
                        onClick={handleDisconnectStripe}
                        data-testid="disconnect-stripe-btn"
                      >
                        Desconectar
                      </Button>
                    </div>

                    {/* Account Details */}
                    <div className="grid gap-4 md:grid-cols-2">
                      <div className="p-4 bg-muted/50 rounded-lg">
                        <p className="text-sm text-muted-foreground mb-1">Estado de cobros</p>
                        <p className={`font-medium ${stripeConnectStatus.charges_enabled ? 'text-green-600' : 'text-amber-600'}`}>
                          {stripeConnectStatus.charges_enabled ? 'Habilitado' : 'Pendiente de verificación'}
                        </p>
                      </div>
                      <div className="p-4 bg-muted/50 rounded-lg">
                        <p className="text-sm text-muted-foreground mb-1">Configuración</p>
                        <p className={`font-medium ${stripeConnectStatus.onboarding_complete ? 'text-green-600' : 'text-amber-600'}`}>
                          {stripeConnectStatus.onboarding_complete ? 'Completa' : 'Requiere más datos'}
                        </p>
                      </div>
                    </div>

                    {!stripeConnectStatus.onboarding_complete && (
                      <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                        <p className="text-sm text-amber-700 dark:text-amber-400">
                          Tu cuenta necesita información adicional para poder recibir pagos.
                        </p>
                        <Button 
                          className="mt-3" 
                          size="sm"
                          onClick={handleConnectStripe}
                          disabled={stripeLoading}
                        >
                          <ExternalLink className="h-4 w-4 mr-2" />
                          Completar configuración
                        </Button>
                      </div>
                    )}

                    <div className="pt-4 border-t">
                      <h4 className="font-medium mb-3">¿Qué puedes hacer con Stripe Connect?</h4>
                      <ul className="space-y-2 text-sm text-muted-foreground">
                        <li className="flex items-center gap-2">
                          <Check className="h-4 w-4 text-green-500" />
                          Recibir pagos de reservaciones con depósito
                        </li>
                        <li className="flex items-center gap-2">
                          <Check className="h-4 w-4 text-green-500" />
                          Vender boletos para eventos
                        </li>
                        <li className="flex items-center gap-2">
                          <Check className="h-4 w-4 text-green-500" />
                          Cobrar experiencias gastronómicas
                        </li>
                        <li className="flex items-center gap-2">
                          <Check className="h-4 w-4 text-green-500" />
                          Pagos directos a tu cuenta bancaria
                        </li>
                      </ul>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {/* Not Connected */}
                    <div className="text-center py-8 border-2 border-dashed rounded-lg">
                      <CreditCard className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                      <h3 className="text-lg font-medium mb-2">Conecta tu cuenta de Stripe</h3>
                      <p className="text-muted-foreground max-w-md mx-auto mb-6">
                        Para recibir pagos de tus clientes por reservaciones, eventos y experiencias, 
                        necesitas conectar una cuenta de Stripe.
                      </p>
                      <Button 
                        onClick={handleConnectStripe}
                        disabled={stripeLoading}
                        className="bg-purple-600 hover:bg-purple-700"
                        data-testid="connect-stripe-btn"
                      >
                        {stripeLoading ? (
                          <>Conectando...</>
                        ) : (
                          <>
                            <Link2 className="h-4 w-4 mr-2" />
                            Conectar con Stripe
                          </>
                        )}
                      </Button>
                    </div>

                    {/* Benefits */}
                    <div className="grid gap-4 md:grid-cols-3">
                      <div className="p-4 bg-muted/50 rounded-lg text-center">
                        <CreditCard className="h-8 w-8 mx-auto mb-2 text-purple-600" />
                        <p className="font-medium text-sm">Depósitos de Reservación</p>
                        <p className="text-xs text-muted-foreground mt-1">Cobra anticipos para reducir no-shows</p>
                      </div>
                      <div className="p-4 bg-muted/50 rounded-lg text-center">
                        <CreditCard className="h-8 w-8 mx-auto mb-2 text-orange-600" />
                        <p className="font-medium text-sm">Venta de Boletos</p>
                        <p className="text-xs text-muted-foreground mt-1">Vende entradas para tus eventos</p>
                      </div>
                      <div className="p-4 bg-muted/50 rounded-lg text-center">
                        <CreditCard className="h-8 w-8 mx-auto mb-2 text-green-600" />
                        <p className="font-medium text-sm">Experiencias</p>
                        <p className="text-xs text-muted-foreground mt-1">Cobra por experiencias gastronómicas</p>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Locations Settings (Multi-location) */}
          <TabsContent value="locations" className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-medium">Ubicaciones</h3>
                <p className="text-sm text-muted-foreground">Gestiona las sucursales de tu restaurante</p>
              </div>
              {(user?.role === 'corporate_admin' || user?.role === 'owner') && (
                <Button size="sm" onClick={() => setShowLocationDialog(true)} data-testid="add-location-btn">
                  <Plus className="h-4 w-4 mr-1" /> Agregar Ubicación
                </Button>
              )}
            </div>

            <div className="grid gap-4">
              {locations.length === 0 ? (
                <Card>
                  <CardContent className="py-8 text-center text-muted-foreground">
                    No hay ubicaciones adicionales configuradas
                  </CardContent>
                </Card>
              ) : (
                locations.map((location) => (
                  <Card key={location.id} data-testid={`location-card-${location.id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center">
                            <MapPin className="h-6 w-6 text-primary" />
                          </div>
                          <div>
                            <h4 className="font-medium">{location.display_name}</h4>
                            {location.address && (
                              <p className="text-sm text-muted-foreground flex items-center gap-1">
                                <Globe className="h-3 w-3" /> {location.address}
                              </p>
                            )}
                            <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                              {location.phone && (
                                <span className="flex items-center gap-1">
                                  <Phone className="h-3 w-3" /> {location.phone}
                                </span>
                              )}
                              {location.email && (
                                <span className="flex items-center gap-1">
                                  <Mail className="h-3 w-3" /> {location.email}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {location.is_current && (
                            <span className="text-xs px-2 py-1 rounded bg-green-500/10 text-green-600">
                              Actual
                            </span>
                          )}
                          {!location.parent_restaurant_id && (
                            <span className="text-xs px-2 py-1 rounded bg-primary/10 text-primary">
                              Principal
                            </span>
                          )}
                          {location.parent_restaurant_id && (user?.role === 'corporate_admin' || user?.role === 'owner') && (
                            <Button 
                              variant="ghost" 
                              size="icon"
                              onClick={() => handleDeleteLocation(location.id)}
                              data-testid={`delete-location-${location.id}`}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Create Location Dialog */}
      <Dialog open={showLocationDialog} onOpenChange={setShowLocationDialog}>
        <DialogContent data-testid="location-dialog">
          <DialogHeader>
            <DialogTitle>Nueva Ubicación</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label>Nombre de la Ubicación *</Label>
              <Input
                value={locationForm.location_name}
                onChange={(e) => setLocationForm({ ...locationForm, location_name: e.target.value })}
                placeholder="Polanco, Roma Norte, Santa Fe..."
                data-testid="location-name-input"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Se mostrará como "{restaurant?.name} - {locationForm.location_name || 'Ubicación'}"
              </p>
            </div>

            <div>
              <Label>Dirección *</Label>
              <Input
                value={locationForm.address}
                onChange={(e) => setLocationForm({ ...locationForm, address: e.target.value })}
                placeholder="Av. Presidente Masaryk 123, Polanco"
                data-testid="location-address-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Teléfono</Label>
                <Input
                  value={locationForm.phone}
                  onChange={(e) => setLocationForm({ ...locationForm, phone: e.target.value })}
                  placeholder="+52 55 1234 5678"
                  data-testid="location-phone-input"
                />
              </div>
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={locationForm.email}
                  onChange={(e) => setLocationForm({ ...locationForm, email: e.target.value })}
                  placeholder="polanco@restaurante.com"
                  data-testid="location-email-input"
                />
              </div>
            </div>

            <div>
              <Label>Zona Horaria</Label>
              <Select 
                value={locationForm.timezone} 
                onValueChange={(value) => setLocationForm({ ...locationForm, timezone: value })}
              >
                <SelectTrigger data-testid="location-timezone-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="America/Mexico_City">Ciudad de México (UTC-6)</SelectItem>
                  <SelectItem value="America/Cancun">Cancún (UTC-5)</SelectItem>
                  <SelectItem value="America/Tijuana">Tijuana (UTC-8)</SelectItem>
                  <SelectItem value="America/Bogota">Bogotá (UTC-5)</SelectItem>
                  <SelectItem value="America/Lima">Lima (UTC-5)</SelectItem>
                  <SelectItem value="Europe/Madrid">Madrid (UTC+1)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLocationDialog(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleCreateLocation}
              disabled={!locationForm.location_name || !locationForm.address}
              data-testid="save-location-btn"
            >
              Crear Ubicación
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create/Edit Time Slot Dialog */}
      <Dialog open={showSlotDialog} onOpenChange={(open) => {
        setShowSlotDialog(open);
        if (!open) {
          setEditingSlot(null);
          resetSlotForm();
        }
      }}>
        <DialogContent data-testid="timeslot-dialog">
          <DialogHeader>
            <DialogTitle>{editingSlot ? 'Editar Turno de Servicio' : 'Nuevo Turno de Servicio'}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label>Nombre del Turno</Label>
              <Input
                value={slotForm.name}
                onChange={(e) => setSlotForm({ ...slotForm, name: e.target.value })}
                placeholder="Almuerzo, Cena, Brunch..."
                data-testid="slot-name-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Hora de Inicio</Label>
                <Input
                  type="time"
                  value={slotForm.start_time}
                  onChange={(e) => setSlotForm({ ...slotForm, start_time: e.target.value })}
                  data-testid="slot-start-time-input"
                />
              </div>
              <div>
                <Label>Hora de Fin</Label>
                <Input
                  type="time"
                  value={slotForm.end_time}
                  onChange={(e) => setSlotForm({ ...slotForm, end_time: e.target.value })}
                  data-testid="slot-end-time-input"
                />
              </div>
            </div>

            <div>
              <Label>Días Disponibles</Label>
              <div className="flex gap-2 mt-2">
                {DAYS.map((day, i) => (
                  <Button
                    key={i}
                    type="button"
                    variant={slotForm.days.includes(i) ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => toggleDay(i)}
                    data-testid={`day-toggle-${i}`}
                  >
                    {day}
                  </Button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Duración por Defecto (min)</Label>
                <Input
                  type="number"
                  value={slotForm.default_duration}
                  onChange={(e) => setSlotForm({ ...slotForm, default_duration: parseInt(e.target.value) })}
                  data-testid="slot-duration-input"
                />
              </div>
              <div>
                <Label>Buffer entre Reservas (min)</Label>
                <Input
                  type="number"
                  value={slotForm.buffer_minutes}
                  onChange={(e) => setSlotForm({ ...slotForm, buffer_minutes: parseInt(e.target.value) })}
                  data-testid="slot-buffer-input"
                />
              </div>
            </div>

            <div className="flex items-center justify-between">
              <Label>Turno Activo</Label>
              <Switch
                checked={slotForm.is_active}
                onCheckedChange={(checked) => setSlotForm({ ...slotForm, is_active: checked })}
                data-testid="slot-active-switch"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowSlotDialog(false);
              setEditingSlot(null);
              resetSlotForm();
            }}>
              {t('common.cancel')}
            </Button>
            <Button 
              onClick={editingSlot ? handleUpdateSlot : handleCreateSlot} 
              disabled={!slotForm.name}
              data-testid={editingSlot ? "update-timeslot-btn" : "create-timeslot-btn"}
            >
              {editingSlot ? t('common.save') : t('common.create')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
